import axios from "axios";
import { apiBaseUrl } from "@/lib/env";

const TOKEN_KEY = "auth_token";
const CONSOLE_ACCESS_KEY = "console_access_mode";

/** Fired when an API 401 clears the session so AuthContext can show the in-app login. */
export const AUTH_EXPIRED_EVENT = "restaurant-erp:auth-expired";
export const CONSOLE_ACCESS_CLOSED_EVENT = "restaurant-erp:console-access-closed";

const api = axios.create({
  baseURL: apiBaseUrl(),
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

export function getAuthToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
}

export function isConsoleAccessMode(): boolean {
  return sessionStorage.getItem(CONSOLE_ACCESS_KEY) === "1";
}

export function setAuthToken(token: string): void {
  clearConsoleAccessSession();
  localStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  localStorage.setItem(TOKEN_KEY, token);
  api.defaults.headers.common.Authorization = `Bearer ${token}`;
}

/** Superadmin store access — isolated to this browser tab; does not touch normal login storage. */
export function setConsoleAccessToken(token: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(CONSOLE_ACCESS_KEY, "1");
  api.defaults.headers.common.Authorization = `Bearer ${token}`;
}

export function clearAuthToken(): void {
  if (isConsoleAccessMode()) {
    clearConsoleAccessSession();
    return;
  }
  localStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  delete api.defaults.headers.common.Authorization;
}

export function clearConsoleAccessSession(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(CONSOLE_ACCESS_KEY);
  delete api.defaults.headers.common.Authorization;
}

function isLoginApiRequest(url?: string): boolean {
  return typeof url === "string" && url.includes("/login");
}

const existing = getAuthToken();
if (existing) {
  api.defaults.headers.common.Authorization = `Bearer ${existing}`;
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !isLoginApiRequest(error.config?.url)) {
      const consoleAccess = isConsoleAccessMode();
      clearAuthToken();
      if (typeof window !== "undefined") {
        const path = window.location.pathname || "";
        if (path.includes("/console") && !path.includes("/console-access")) {
          return Promise.reject(error);
        }
        if (consoleAccess) {
          window.dispatchEvent(new Event(CONSOLE_ACCESS_CLOSED_EVENT));
          const base = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");
          window.location.assign(base ? `${base}/console-access/closed` : "/console-access/closed");
        } else {
          window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
        }
      }
    }
    return Promise.reject(error);
  },
);

export default api;
